import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  HeaderGroup,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState
} from '@tanstack/react-table'
import classNames from 'classnames'

import { pageLimitOptions } from '@/src/constants'
import { useCustomRouter } from '@hook'
import { CheckBox } from 'grommet'
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Pagination } from '../Pagination'

type TableProps<TData, TValue> = {
  columns?: ColumnDef<TData, TValue>[]
  data?: TData[]
  isLoading?: boolean
  emptyState?: ReactNode
  emptyDescription?: string
  message?: string
  wrapperClass?: string
  title?: string | ReactNode
  className?: string
  onRowClick?: (val: TData) => void
  pageCount?: number
  showPagination?: boolean
  showSelection?: boolean
  onSelectionChange?: (selection: RowSelectionState) => void
}

export const EmptyTableState = ({ message, emptyDescription }: { message?: string; emptyDescription?: string }) => (
  <div className="flex flex-col">
    <img alt="image empty states" className="mx-auto" height={200} src="/not_found.png" width={200} />
    <p className="text-2xl font-semibold">{message || 'You haven’t added any data yet.'}.</p>
    <p className="text-base tracking-normal text-[#475367]">
      {emptyDescription || 'You haven’t added any data. Add data to start  managing them.'}
    </p>
  </div>
)

export const Table2 = <TData, TValue>({
  columns = [],
  data = [],
  className,
  isLoading,
  emptyState,
  onRowClick,
  pageCount = 1,
  wrapperClass,
  title,
  showPagination = true,
  showSelection = false,
  onSelectionChange,
  message,
  emptyDescription
}: TableProps<TData, TValue>) => {
  const router = useCustomRouter()
  const searchParams = useSearchParams()[0]
  const page = Number(searchParams.get('page')?.toString() || 1)
  const pageLimit = Number(searchParams.get('limit')?.toString() || 20)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [pagination, setPagination] = useState({
    pageIndex: page - 1,
    pageSize: pageLimit
  })

  useEffect(() => {
    onSelectionChange?.(rowSelection)
  }, [rowSelection, onSelectionChange])

  const finalColumns = useMemo(() => {
    if (!showSelection) return columns

    const selectionColumn: ColumnDef<TData, any> = {
      id: 'select',
      header: ({ table }) => (
        <CheckBox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <CheckBox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      )
    }

    return [selectionColumn, ...columns]
  }, [columns, showSelection])

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageIndex: page - 1,
      pageSize: pageLimit
    }))
  }, [page, pageLimit])

  const table = useReactTable<TData>({
    columns: finalColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(pageCount / pageLimit),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getRowId: (row: any) => row?.id,
    state: {
      columnFilters,
      columnVisibility,
      pagination,
      rowSelection,
      sorting
    }
  })

  const isEmpty = table.options.data?.length === 0
  const colSpan = table.options.columns.length
  return (
    <div className={classNames('flex-1 overflow-x-auto', wrapperClass)}>
      {title && <h2 className="mb-4 pb-2 text-xl font-bold">{title}</h2>}

      <div
        className={classNames(
          'flex-1 flex-col flex overflow-x-auto !capitalize rounded-lg border border-gray-200 min-h-[300px]',
          className
        )}
      >
        <table className="h-full w-full">
          <thead className="bg-[#F5F5F5]">
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => (
              <tr className="overflow-hidden" key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th className="whitespace-nowrap p-3 text-left text-sm font-medium text-black" key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y-4 divide-white">
            {isLoading ? (
              <TableLoading colSpan={colSpan} />
            ) : isEmpty ? (
              <tr>
                <td className="mx-auto w-full py-12 text-center" colSpan={colSpan}>
                  {emptyState || <EmptyTableState emptyDescription={emptyDescription} message={message} />}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row: Row<TData>) => (
                <tr
                  className="rounded-lg odd:bg-black/[1%] even:bg-[#F7FBFF]"
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      className={classNames('px-3 py-2.5  text-sm text-gray-800 first:rounded-s-xl last:rounded-e-xl', {
                        'cursor-pointer': !!onRowClick
                      })}
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {showPagination && (
          <div className="my-5 px-4 mt-auto items-end">
            <Pagination
              count={pageCount}
              limit={pageLimit}
              options={pageLimitOptions}
              page={page}
              setPage={(pageNumber: number) => {
                router.push(undefined, { page: String(pageNumber) })
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export const TableLoading = ({ colSpan }: { colSpan: number }) => (
  <>
    {[1, 2, 3, 4, 5].map(val => (
      <Fragment key={val}>
        <tr className="animate-pulse odd:bg-black/[1%]">
          <td className="text-brand-textBlack w-max max-w-full px-4 py-6 text-left text-sm" colSpan={colSpan} />
        </tr>
        <tr className="animate-pulse even:bg-[#F7FBFF]">
          <td className="text-brand-textBlack w-max max-w-full px-4 py-6 text-left text-sm" colSpan={colSpan} />
        </tr>
      </Fragment>
    ))}
  </>
)
